// /mfa-legalisation/documents — Wave 63: TH hub of the six MFA legalisation document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/mfa-legalisation/documents`;
const TITLE = "เอกสารที่ต้องรับรองกรมการกงสุล (MFA) — ครบ 6 กลุ่ม | NYC";
const DESC =
  "รวมเอกสารที่ต้องผ่านงานนิติกรณ์กรมการกงสุล กระทรวงการต่างประเทศ 6 กลุ่ม พร้อมลำดับขั้นตอน เช็กลิสต์เตรียมเอกสาร และข้อควรรู้ก่อนยื่นสถานทูตปลายทาง";

export const Route = createFileRoute("/mfa-legalisation/documents/")({
  loader: async () => {
    const { MFA_DOC_GROUPS } = await import("@/data/mfa-doc-groups");
    return {
      __d: { MFA_DOC_GROUPS },
      groups: MFA_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, "/mfa-legalisation/documents", "th") },
      { name: "description", content: ctaDesc(DESC, "/mfa-legalisation/documents", "th") },
      { property: "og:title", content: ctaTitle(TITLE, "/mfa-legalisation/documents", "th") },
      { property: "og:description", content: ctaDesc(DESC, "/mfa-legalisation/documents", "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/mfa-legalisation/documents` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารที่ต้องรับรองนิติกรณ์กรมการกงสุล",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: MfaDocumentsPage,
});

function MfaDocumentsPage() {
  const { __d: { MFA_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/mfa-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/mfa-legalisation" className="hover:underline">
          รับรองเอกสาร MFA + สถานทูต
        </Link>{" "}
        / เอกสารที่ต้องรับรอง
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        เอกสารที่ต้องรับรองกรมการกงสุล (MFA) — ครบ 6 กลุ่ม
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        งานนิติกรณ์เอกสารของกรมการกงสุล กระทรวงการต่างประเทศ คือการรับรองลายมือชื่อและตราประทับของเจ้าหน้าที่
        ผู้ออกเอกสาร ไม่ใช่การรับรองข้อเท็จจริงในเอกสาร เอกสารราชการจึงยื่นได้โดยตรง ส่วนเอกสารเอกชน
        ต้องผ่านการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารก่อน หน้านี้แยกเอกสารตามกลุ่มการใช้งาน
        พร้อมลำดับขั้นตอนและเช็กลิสต์ของแต่ละกลุ่ม
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>สถานะ Apostille ของไทย:</strong> ไทยยื่นภาคยานุวัติสารเมื่อ {APOSTILLE_STATUS.accessionDepositTh}{" "}
        และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ {APOSTILLE_STATUS.entryIntoForceTh} ก่อนถึงวันดังกล่าว
        เอกสารยังต้องผ่านกรมการกงสุลและสถานทูตประเทศปลายทางตามเดิม{" "}
        <a href={APOSTILLE_STATUS.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
          ที่มา: HCCH
        </a>
      </aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {MFA_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/mfa-legalisation/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {MFA_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/mfa-legalisation/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านรายละเอียดกลุ่ม {g.titleTh} →
          </Link>
        </section>
      ))}
          <ServiceAuthorityBlock topic="mfa" locale="th" />
    </main>
  );
}
